import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash, X, ArrowLeft, Download, CaretRight, User, Lightning, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const QTYPES = { rating: "Valutazione (1-5)", rating10: "Valutazione (0-10)", choice: "Scelta multipla", open: "Risposta aperta", suggestion: "Suggerimento" };

export default function Sondaggi() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [invites, setInvites] = useState([]);
  const [tab, setTab] = useState("sondaggi");
  const [showNew, setShowNew] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", questions: [], auto_gradimento: false });
  const [qForm, setQForm] = useState({ type: "rating", text: "", options: "" });
  const [resultsFor, setResultsFor] = useState(null);
  const [results, setResults] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/surveys").then((r) => setSurveys(r.data)).catch(() => {});
    api.get("/survey-invites").then((r) => setInvites(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const addQuestion = () => {
    if (!qForm.text.trim()) return;
    setForm((f) => ({
      ...f,
      questions: [...f.questions, {
        type: qForm.type, text: qForm.text,
        options: qForm.type === "choice" ? qForm.options.split(",").map((s) => s.trim()).filter(Boolean) : [],
      }],
    }));
    setQForm({ type: "rating", text: "", options: "" });
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({ name: s.name, description: s.description || "", questions: s.questions || [], auto_gradimento: !!s.auto_gradimento });
    setShowNew(true);
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.patch(`/surveys/${editId}`, form);
        toast.success("Sondaggio aggiornato");
      } else {
        await api.post("/surveys", form);
        toast.success("Sondaggio creato");
      }
      setShowNew(false);
      setEditId(null);
      setForm({ name: "", description: "", questions: [], auto_gradimento: false });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openResults = (s) => {
    setResultsFor(s);
    setDetail(null);
    api.get(`/surveys/${s.id}/results`).then((r) => setResults(r.data)).catch((e) => toast.error(apiError(e)));
  };

  const exportCsv = async () => {
    try {
      const r = await api.get(`/surveys/${resultsFor.id}/results.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `risultati-${resultsFor.name}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("it-IT",
    { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const scoreCls = (v) => {
    const n = parseFloat(v);
    if (Number.isNaN(n)) return "text-zinc-400";
    if (n <= 5) return n >= 4 ? "text-emerald-600" : n >= 3 ? "text-amber-500" : "text-rose-600";
    return n >= 9 ? "text-emerald-600" : n >= 7 ? "text-amber-500" : "text-rose-600";
  };

  const btnCls = "h-10 px-4 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:border-zinc-400 flex items-center gap-1.5";

  // ---- Vista Risultati / Dettaglio risposta (stile Cooperto) ----
  if (resultsFor) {
    return (
      <div className="space-y-4" data-testid="survey-results-page">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900" data-testid="results-title">
          {detail ? `Risposta al sondaggio "${resultsFor.name}"` : `Risultati sondaggio "${resultsFor.name}"`}
        </h1>
        <div className="flex gap-2 flex-wrap">
          {detail ? (
            <>
              <button onClick={() => setDetail(null)} data-testid="results-back" className={btnCls}>
                <ArrowLeft size={14} weight="bold" /> Torna ai risultati
              </button>
              <button onClick={() => navigate(`/clienti/${detail.customer_id}`)} data-testid="results-profile" className={btnCls}>
                <User size={14} weight="bold" /> Profilo contatto
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setResultsFor(null)} data-testid="results-back-surveys" className={btnCls}>
                <ArrowLeft size={14} weight="bold" /> Torna ai sondaggi
              </button>
              <button onClick={exportCsv} data-testid="results-export" className={btnCls}>
                <Download size={14} weight="bold" /> Esporta risultati
              </button>
            </>
          )}
        </div>

        {detail ? (
          <div className="bg-white border border-zinc-200 rounded-lg" data-testid="response-detail">
            <div className="grid grid-cols-[1fr_1fr] px-4 py-2.5 border-b border-zinc-100 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <span>Domanda</span><span>Risposta</span>
            </div>
            <div className="divide-y divide-zinc-100">
              {detail.answers.map((a, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr] gap-3 px-4 py-3" data-testid={`answer-row-${i}`}>
                  <span className="text-sm text-zinc-600">{a.question}</span>
                  <span className="text-sm font-semibold text-zinc-900">{String(a.value ?? "—")}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-lg" data-testid="results-list">
            <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 px-4 py-2.5 border-b border-zinc-100 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <span>Data</span><span>Contatto</span><span>Votazione</span><span>Azioni</span>
            </div>
            <div className="divide-y divide-zinc-100">
              {results.length === 0 && (
                <div className="py-8 text-center text-sm text-zinc-400">Nessuna compilazione per questo sondaggio</div>
              )}
              {results.map((r) => (
                <div key={r.id} className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 items-center px-4 py-3" data-testid={`result-row-${r.id}`}>
                  <span className="text-sm text-zinc-600">{fmt(r.submitted_at)}</span>
                  <span className="text-sm font-semibold text-zinc-900 truncate">{r.customer_name}</span>
                  <span className={`font-display font-bold text-lg ${scoreCls(r.votazione)}`} data-testid={`result-score-${r.id}`}>
                    {r.votazione ?? "—"}
                  </span>
                  <button onClick={() => setDetail(r)} data-testid={`result-open-${r.id}`}
                    className="p-2 text-zinc-400 hover:text-zinc-900 transition">
                    <CaretRight size={18} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Vista principale ----
  return (
    <div className="space-y-4" data-testid="sondaggi-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Sondaggi</h1>
        <button onClick={() => { setEditId(null); setForm({ name: "", description: "", questions: [], auto_gradimento: false }); setShowNew(true); }} data-testid="btn-nuovo-sondaggio"
          className="h-11 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2">
          <Plus size={16} weight="bold" /> Nuovo
        </button>
      </div>

      <div className="flex gap-1.5 overflow-x-auto">
        {[["sondaggi", "Sondaggi"], ["inviti", `Inviti (${invites.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`shrink-0 h-9 px-4 rounded-full text-xs font-semibold border ${tab === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "sondaggi" && (
        <div className="bg-white border border-zinc-200 rounded-lg" data-testid="surveys-list">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-2.5 border-b border-zinc-100 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            <span>Nome</span><span>Stato</span><span>Risposte</span><span>Azioni</span>
          </div>
          <div className="divide-y divide-zinc-100">
            {surveys.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessun sondaggio</div>}
            {surveys.map((s) => (
              <div key={s.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center px-4 py-3" data-testid={`survey-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate flex items-center gap-2">
                    {s.name}
                    {s.auto_gradimento && (
                      <span className="text-[10px] font-bold bg-cyan-50 text-cyan-800 border border-cyan-200 rounded-full px-2 py-0.5 flex items-center gap-1" data-testid={`survey-auto-${s.id}`}>
                        <Lightning size={10} weight="fill" /> Auto 12h dopo OUT
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500">{s.questions.length} domande · {s.invites} inviti</div>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                  s.active ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-zinc-100 text-zinc-500 border-zinc-300"}`}>
                  {s.active ? "Attivo" : "Disattivato"}
                </span>
                <span className="text-sm font-bold text-zinc-900">{s.responses}</span>
                <div className="flex gap-1.5">
                  <button onClick={() => openEdit(s)} data-testid={`survey-edit-${s.id}`}
                    className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-bold text-zinc-700 hover:border-zinc-400 flex items-center gap-1">
                    <PencilSimple size={13} weight="bold" /> Modifica
                  </button>
                  <button onClick={() => openResults(s)} data-testid={`survey-results-${s.id}`}
                    className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold">
                    Risultati
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "inviti" && (
        <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="invites-list">
          {invites.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessun invito inviato</div>}
          {invites.map((i) => (
            <div key={i.id} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-zinc-900">{i.survey_name}</div>
                <div className="text-xs text-zinc-500">{i.customer_name}</div>
              </div>
              <a href={`/s/${i.token}`} target="_blank" rel="noreferrer" className="text-xs font-mono text-zinc-500 underline truncate max-w-[120px]">
                /s/{i.token.slice(0, 8)}…
              </a>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                i.completed ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}>
                {i.completed ? "Compilato" : "In attesa"}
              </span>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowNew(false)}>
          <form onSubmit={create} className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="survey-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">{editId ? "Modifica sondaggio" : "Nuovo sondaggio"}</h3>
              <button type="button" onClick={() => { setShowNew(false); setEditId(null); }} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome sondaggio"
              data-testid="survey-name" className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm" />
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descrizione"
              data-testid="survey-desc" className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm" />
            <label className="flex items-center gap-2 text-sm text-zinc-700 bg-cyan-50 border border-cyan-200 rounded-md px-3 py-2.5">
              <input type="checkbox" checked={form.auto_gradimento}
                onChange={(e) => setForm({ ...form, auto_gradimento: e.target.checked })}
                data-testid="survey-auto" className="w-4 h-4 accent-cyan-600" />
              <span><strong>Invio automatico</strong> — parte 12 ore dopo il passaggio del cliente da IN a OUT</span>
            </label>

            <div className="border border-zinc-200 rounded-md p-3 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Domande ({form.questions.length})</div>
              {form.questions.map((q, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-zinc-50 rounded px-2 py-1.5">
                  <span className="flex-1">{q.text} <span className="text-xs text-zinc-400">({QTYPES[q.type]})</span></span>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, questions: f.questions.filter((_, x) => x !== i) }))}
                    className="text-zinc-400 hover:text-rose-600"><Trash size={14} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <select value={qForm.type} onChange={(e) => setQForm({ ...qForm, type: e.target.value })} data-testid="q-type"
                  className="h-10 px-2 rounded-md border border-zinc-300 text-xs bg-white">
                  {Object.entries(QTYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input value={qForm.text} onChange={(e) => setQForm({ ...qForm, text: e.target.value })} placeholder="Testo domanda"
                  data-testid="q-text" className="flex-1 h-10 px-2 rounded-md border border-zinc-300 text-sm" />
              </div>
              {qForm.type === "choice" && (
                <input value={qForm.options} onChange={(e) => setQForm({ ...qForm, options: e.target.value })}
                  placeholder="Opzioni separate da virgola" data-testid="q-options"
                  className="w-full h-10 px-2 rounded-md border border-zinc-300 text-sm" />
              )}
              <button type="button" onClick={addQuestion} data-testid="q-add"
                className="h-9 px-3 rounded-md bg-zinc-100 text-xs font-semibold text-zinc-700">+ Aggiungi domanda</button>
            </div>
            <button className="w-full h-12 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid="survey-submit">{editId ? "Salva modifiche" : "Crea sondaggio"}</button>
          </form>
        </div>
      )}
    </div>
  );
}
